import { apiFetch } from '../../lib/apiClient';
import type {
  LearningPageData,
  LineupLearnedResult,
  LineupQueueMutation,
  QueueSource,
  RoleQueueMutation,
  TeamPlayer,
} from './types';

function teamPath(teamId: string): string {
  return `/teams/${encodeURIComponent(teamId)}`;
}

export function getLearning(teamId: string, playerId?: string): Promise<LearningPageData> {
  const params = new URLSearchParams();
  if (playerId) params.set('playerId', playerId);
  const suffix = params.toString();
  return apiFetch<LearningPageData>(`${teamPath(teamId)}/learning${suffix ? `?${suffix}` : ''}`);
}

export function listPlayers(teamId: string): Promise<TeamPlayer[]> {
  return apiFetch<TeamPlayer[]>(`${teamPath(teamId)}/players`);
}

function queueBody(playerId?: string, source?: QueueSource): { playerId?: string; source?: QueueSource } {
  return {
    ...(playerId ? { playerId } : {}),
    ...(source ? { source } : {}),
  };
}

export function addRoleToQueue(
  teamId: string,
  strategyRoleId: string,
  playerId?: string,
  source?: QueueSource,
): Promise<RoleQueueMutation> {
  return apiFetch<RoleQueueMutation>(`${teamPath(teamId)}/learning/roles`, {
    method: 'POST',
    body: { strategyRoleId, ...queueBody(playerId, source) },
  });
}

export function removeRoleFromQueue(
  teamId: string,
  strategyRoleId: string,
  playerId?: string,
): Promise<RoleQueueMutation> {
  const params = new URLSearchParams();
  if (playerId) params.set('playerId', playerId);
  const suffix = params.toString();
  return apiFetch<RoleQueueMutation>(
    `${teamPath(teamId)}/learning/roles/${encodeURIComponent(strategyRoleId)}${suffix ? `?${suffix}` : ''}`,
    { method: 'DELETE' },
  );
}

export function reorderRoleQueue(
  teamId: string,
  strategyRoleIds: string[],
  playerId?: string,
): Promise<RoleQueueMutation> {
  return apiFetch<RoleQueueMutation>(`${teamPath(teamId)}/learning/roles/order`, {
    method: 'PUT',
    body: { strategyRoleIds, ...(playerId ? { playerId } : {}) },
  });
}

export function setStrategyQueued(
  teamId: string,
  strategyId: string,
  queued: boolean,
  playerId?: string,
): Promise<RoleQueueMutation> {
  return apiFetch<RoleQueueMutation>(
    `${teamPath(teamId)}/learning/strategies/${encodeURIComponent(strategyId)}`,
    { method: 'POST', body: { queued, ...(playerId ? { playerId } : {}) } },
  );
}

export function clearDoneRoles(teamId: string, map?: string, playerId?: string): Promise<RoleQueueMutation> {
  return apiFetch<RoleQueueMutation>(`${teamPath(teamId)}/learning/roles/clear-done`, {
    method: 'POST',
    body: { ...(playerId ? { playerId } : {}), ...(map ? { map } : {}) },
  });
}

export function addLineupToQueue(
  teamId: string,
  utilityUseId: string,
  playerId?: string,
  source?: QueueSource,
): Promise<LineupQueueMutation> {
  return apiFetch<LineupQueueMutation>(`${teamPath(teamId)}/learning/lineups`, {
    method: 'POST',
    body: { utilityUseId, ...queueBody(playerId, source) },
  });
}

export function removeLineupFromQueue(
  teamId: string,
  utilityUseId: string,
  playerId?: string,
): Promise<LineupQueueMutation> {
  const params = new URLSearchParams();
  if (playerId) params.set('playerId', playerId);
  const suffix = params.toString();
  return apiFetch<LineupQueueMutation>(
    `${teamPath(teamId)}/learning/lineups/${encodeURIComponent(utilityUseId)}${suffix ? `?${suffix}` : ''}`,
    { method: 'DELETE' },
  );
}

export function reorderLineupQueue(
  teamId: string,
  utilityUseIds: string[],
  playerId?: string,
): Promise<LineupQueueMutation> {
  return apiFetch<LineupQueueMutation>(`${teamPath(teamId)}/learning/lineups/order`, {
    method: 'PUT',
    body: { utilityUseIds, ...(playerId ? { playerId } : {}) },
  });
}

export function clearDoneLineups(teamId: string, map?: string, playerId?: string): Promise<LineupQueueMutation> {
  return apiFetch<LineupQueueMutation>(`${teamPath(teamId)}/learning/lineups/clear-done`, {
    method: 'POST',
    body: { ...(playerId ? { playerId } : {}), ...(map ? { map } : {}) },
  });
}

export function markLineupLearned(teamId: string, utilityUseId: string): Promise<LineupLearnedResult> {
  return apiFetch<LineupLearnedResult>(
    `${teamPath(teamId)}/learning/lineups/${encodeURIComponent(utilityUseId)}/learned`,
    { method: 'PUT' },
  );
}

export function unmarkLineupLearned(teamId: string, utilityUseId: string): Promise<LineupLearnedResult> {
  return apiFetch<LineupLearnedResult>(
    `${teamPath(teamId)}/learning/lineups/${encodeURIComponent(utilityUseId)}/learned`,
    { method: 'DELETE' },
  );
}
